/** =============================
 *  ENVS / DRIVERS / WALLETS
 *  ============================= */
export type TunaEnv = 'sandbox' | 'production';

export const PaymentDriver = {
  TUNA: 'TUNA',
  PAGARME: 'PAGARME',
  YOOGA_PAYMENTS: 'YOOGA_PAYMENTS',
} as const;
export type PaymentDriver = typeof PaymentDriver[keyof typeof PaymentDriver];

export const WalletKind = {
  GOOGLE_PAY: 'GOOGLE_PAY',
  APPLE_PAY: 'APPLE_PAY',
} as const;
export type WalletKind = typeof WalletKind[keyof typeof WalletKind];

/** =============================
 *  TUNA SDK (window.Tuna)
 *  ============================= */
export interface TunaCtor {
  (sessionId: string, env: TunaEnv): TunaInstance;
}

declare global {
  interface Window {
    Tuna?: (sessionId: string, env: 'sandbox' | 'production') => TunaInstance;
    ApplePaySession?: any;
    google?: {
      payments?: {
        api?: {
          PaymentsClient: new (config: { environment: string }) => {
            isReadyToPay: (request: any) => Promise<{ result: boolean }>;
            loadPaymentData: (request: any) => Promise<any>;
          };
        };
      };
    };
  }
}

/** Estruturas esperadas pela lib Tuna */
export interface TunaGooglePayMerchant {
  merchantName: string;
  merchantId: string;
}

export interface TunaGooglePayOptions {
  // shape compatível com gpay; usamos apenas o que precisamos
  transactionInfo: {
    totalPriceStatus: 'FINAL' | 'ESTIMATED';
    totalPrice: string; // "123.45"
    currencyCode: 'BRL' | string;
  };
  // ...outros campos opcionais do integrador
}

export interface TunaApplePayButtonStyle {
  buttonStyle?: "black" | "white" | "white-outline";
  type?: "buy" | "check-out" | "donate" | "plain";
  locale?: string;
  width?: string;
  height?: string;
  borderRadius?: string;
  padding?: string;
  boxSizing?: string;
}

export interface TunaApplePayButton {
  // a lib usa "buttom" mesmo
  buttom: { 
    selector: string;
    style?: TunaApplePayButtonStyle;
  };
}
export interface TunaApplePayCart {
  lineItems: Array<{ label: string; amount: string }>;
  total: { label: string; amount: string };
}
export interface TunaApplePayOptions extends TunaApplePayButton {
  merchantId: string;
  applePayCallback: (tokenPayload: unknown) => boolean;
  cart: TunaApplePayCart;
}

export interface TunaInstance {
  useGooglePay(
    selector: string,
    merchant: TunaGooglePayMerchant,
    onTokenize: (tokenPayload: unknown) => void,
    options?: Record<string, unknown> | TunaGooglePayOptions
  ): void;

  useApplePay(options: TunaApplePayOptions): Promise<boolean>;
}

/** =============================
 *  SERVICE PARAMS (DTOs)
 *  ============================= */
export interface MountGooglePayParams {
  containerSelector: string;
  sessionId: string;
  totalAmountBRL: number;
  onTokenized: (cardId: string) => void;
}

export interface MountApplePayParams {
  containerSelector: string;
  sessionId: string;
  totalAmountBRL: number;
  onTokenized: (cardId: string | false) => void;
}

export type NewSessionWire =
  | { sessionId: string; hasExternalToken?: boolean; code?: number; message?: string }
  | { token: string }
  | string;

/** =============================
 *  INTENT / CHECKOUT (opcional)
 *  ============================= */
export interface TransactionIntentRequest {
  driver: PaymentDriver;
  method?: WalletKind;
  value: number;
  currency: 'BRL' | string;
  customerId: string;
  cardId: string;
  tokenSession: string;
  correlationId?: string;
}

export interface TransactionIntentResponse {
  id?: string;
  status?: string;
}
